'use client';

import { useState, useRef, useEffect } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/navigation';

import { ChevronDown, Menu, Moon, Sun, X } from 'lucide-react';

import AutoHeightImage from '@/components/cards/AutoHeightImage';
import { useTheme } from '@/context/ThemeContext';
import { CATEGORY_NAMES, CHANNEL_NAMES, CHANNEL_KEYS } from '@/data/credit-cards/constants/keys';

export default function Navbar() {
  const router = useRouter();
  const { theme, toggleTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCategoryOpen, setMobileCategoryOpen] = useState(false);
  const [mobileChannelOpen, setMobileChannelOpen] = useState(false);
  const [mobileBlogOpen, setMobileBlogOpen] = useState(false);

  // Desktop dropdown states
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [channelDropdownOpen, setChannelDropdownOpen] = useState(false);

  // Refs for dropdown containers
  const categoryDropdownRef = useRef<HTMLDivElement>(null);
  const channelDropdownRef = useRef<HTMLDivElement>(null);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    // Reset all sub-menus when toggling the main menu
    if (!mobileMenuOpen) {
      setMobileCategoryOpen(false);
      setMobileChannelOpen(false);
      setMobileBlogOpen(false);
    }
    // Close desktop dropdowns
    setCategoryDropdownOpen(false);
    setChannelDropdownOpen(false);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        categoryDropdownRef.current &&
        !categoryDropdownRef.current.contains(event.target as Node)
      ) {
        setCategoryDropdownOpen(false);
      }
      if (
        channelDropdownRef.current &&
        !channelDropdownRef.current.contains(event.target as Node)
      ) {
        setChannelDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleMobileCategory = () => {
    setMobileCategoryOpen(!mobileCategoryOpen);
  };

  const toggleMobileChannel = () => {
    setMobileChannelOpen(!mobileChannelOpen);
  };

  const toggleMobileBlog = () => {
    setMobileBlogOpen(!mobileBlogOpen);
  };

  return (
    <div className="sticky top-0 z-50 shadow-sm w-full bg-white dark:bg-gray-900 ">
      <div className="mx-auto max-w-7xl">
        <div className="flex h-20 justify-between items-center">
          {/* Logo on the left */}
          <div className="flex items-center pl-6">
            <Link href="/" className="block">
              <AutoHeightImage src={'/logo.png'} alt="Logo" width={100} priority={false} />
            </Link>
          </div>

          {/* Navigation links and theme toggle on the right */}
          <div className="flex items-center space-x-4 p-4">
            {/* Desktop Navigation */}
            <div className="hidden md:flex space-x-6 px-4">
              <Link
                href="/credit-cards"
                className="text-sm text-gray-700 dark:text-gray-200 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium "
              >
                信用卡總覽
              </Link>

              {/* 信用卡分類 with dropdown */}
              <div className="relative" ref={categoryDropdownRef}>
                <div className="flex items-center space-x-1">
                  <button
                    className="text-sm text-gray-700 dark:text-gray-200 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium bg-transparent border-none cursor-pointer"
                    onClick={() => router.push('/credit-cards/category')}
                  >
                    信用卡分類
                  </button>
                  <button
                    className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={() => {
                      setCategoryDropdownOpen(!categoryDropdownOpen);
                      setChannelDropdownOpen(false);
                    }}
                  >
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${categoryDropdownOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                </div>
                {categoryDropdownOpen && (
                  <div className="absolute left-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 transition-all duration-200 border border-gray-200 dark:border-gray-700">
                    <div className="grid grid-cols-2 gap-1 p-2">
                      {Object.entries(CATEGORY_NAMES).map(([key, name]) => (
                        <Link
                          key={key}
                          href={`/credit-cards/category/${key.replace(/^\[|\]$/g, '')}`}
                          className="flex items-center px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400 rounded-md transition-colors cursor-pointer"
                          onClick={() => setCategoryDropdownOpen(false)}
                        >
                          <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                          {name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* 信用卡通路 with dropdown */}
              <div className="relative" ref={channelDropdownRef}>
                <div className="flex items-center space-x-1">
                  <button
                    className="text-sm text-gray-700 dark:text-gray-200 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium bg-transparent border-none cursor-pointer"
                    onClick={() => router.push('/credit-cards/channel')}
                  >
                    信用卡通路
                  </button>
                  <button
                    className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={() => {
                      setChannelDropdownOpen(!channelDropdownOpen);
                      setCategoryDropdownOpen(false);
                    }}
                  >
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${channelDropdownOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                </div>
                {channelDropdownOpen && (
                  <div className="absolute left-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 transition-all duration-200 border border-gray-200 dark:border-gray-700">
                    <div className="max-h-[calc(100vh-200px)] overflow-y-auto overscroll-contain">
                      {/* Online Shopping */}
                      <div className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80 sticky top-0">
                        {CHANNEL_NAMES[CHANNEL_KEYS.ONLINE_SHOPPING]}
                      </div>
                      <div className="grid grid-cols-2 gap-1 p-2">
                        {[
                          CHANNEL_KEYS.ONLINE_SHOPPING,
                          CHANNEL_KEYS.SHOPEE,
                          CHANNEL_KEYS.MOMO,
                          CHANNEL_KEYS.PCHOME,
                        ].map(key => (
                          <Link
                            key={key}
                            href={`/credit-cards/channel/${key}`}
                            className="flex items-center px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400 rounded-md transition-colors cursor-pointer"
                          >
                            <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                            {CHANNEL_NAMES[key]}
                          </Link>
                        ))}
                      </div>

                      {/* Travel */}
                      <div className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80 sticky top-0">
                        {CHANNEL_NAMES[CHANNEL_KEYS.TRAVEL]}
                      </div>
                      <div className="grid grid-cols-2 gap-1 p-2">
                        {[
                          CHANNEL_KEYS.TRAVEL,
                          CHANNEL_KEYS.KKDAY,
                          CHANNEL_KEYS.KLOOK,
                          CHANNEL_KEYS.AGODA,
                          CHANNEL_KEYS.BOOKING,
                        ].map(key => (
                          <Link
                            key={key}
                            href={`/credit-cards/channel/${key}`}
                            className="flex items-center px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400 rounded-md transition-colors cursor-pointer"
                          >
                            <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                            {CHANNEL_NAMES[key]}
                          </Link>
                        ))}
                      </div>

                      {/* Dining */}
                      <div className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80 sticky top-0">
                        {CHANNEL_NAMES[CHANNEL_KEYS.DINING]}
                      </div>
                      <div className="grid grid-cols-2 gap-1 p-2">
                        {[
                          CHANNEL_KEYS.DINING,
                          CHANNEL_KEYS.FOOD_DELIVERY,
                          CHANNEL_KEYS.RESTAURANT,
                        ].map(key => (
                          <Link
                            key={key}
                            href={`/credit-cards/channel/${key}`}
                            className="flex items-center px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400 rounded-md transition-colors cursor-pointer"
                          >
                            <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                            {CHANNEL_NAMES[key]}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* 信用卡文章 */}
              <Link
                href="/credit-cards/blog"
                className="text-sm text-gray-700 dark:text-gray-200 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium cursor-pointer"
              >
                信用卡文章
              </Link>

              {/* <Link
                href='/expiry'
                className='text-sm text-gray-700 dark:text-gray-200 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium '
              >
                產品到期提醒工具
              </Link> */}
            </div>

            {/* Theme Toggle Button */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 rs"
              aria-label={theme === 'dark' ? '切換至亮色模式' : '切換至暗色模式'}
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rs"
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white dark:bg-gray-900 py-4 px-2  border-t border-gray-200 dark:border-gray-800">
            <nav className="flex flex-col space-y-2">
              <Link
                href="/credit-cards"
                className="text-gray-700 dark:text-gray-200 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium  px-4 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
                onClick={() => setMobileMenuOpen(false)}
              >
                信用卡總覽
              </Link>

              {/* Mobile Category Dropdown */}
              <div className="flex flex-col">
                <button
                  onClick={toggleMobileCategory}
                  className="flex justify-between items-center w-full text-left text-gray-700 dark:text-gray-200 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium  px-4 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <span>信用卡分類</span>
                  <ChevronDown className={`h-4 w-4  ${mobileCategoryOpen ? 'rotate-180' : ''}`} />
                </button>

                {mobileCategoryOpen && (
                  <div className="pl-4 mt-1 bg-gray-50 dark:bg-gray-800/50 rounded-lg p-2">
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(CATEGORY_NAMES).map(([key, name]) => (
                        <Link
                          key={key}
                          href={`/credit-cards/category/${key.replace(/^\[|\]$/g, '')}`}
                          className="flex items-center px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400 rounded-md transition-colors cursor-pointer"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                          {name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Mobile Channel Dropdown */}
              <div className="flex flex-col">
                <button
                  onClick={toggleMobileChannel}
                  className="flex justify-between items-center w-full text-left text-gray-700 dark:text-gray-200 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium  px-4 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <span>信用卡通路</span>
                  <ChevronDown className={`h-4 w-4  ${mobileChannelOpen ? 'rotate-180' : ''}`} />
                </button>

                {mobileChannelOpen && (
                  <div className="pl-4 mt-1 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    {/* Online Shopping */}
                    <div className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">
                      {CHANNEL_NAMES[CHANNEL_KEYS.ONLINE_SHOPPING]}
                    </div>
                    <div className="grid grid-cols-2 gap-1 p-2">
                      {[
                        CHANNEL_KEYS.ONLINE_SHOPPING,
                        CHANNEL_KEYS.SHOPEE,
                        CHANNEL_KEYS.MOMO,
                        CHANNEL_KEYS.PCHOME,
                      ].map(key => (
                        <Link
                          key={key}
                          href={`/credit-cards/channel/${key}`}
                          className="flex items-center px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400 rounded-md transition-colors cursor-pointer"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                          {CHANNEL_NAMES[key]}
                        </Link>
                      ))}
                    </div>

                    {/* Travel */}
                    <div className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700 mt-2">
                      {CHANNEL_NAMES[CHANNEL_KEYS.TRAVEL]}
                    </div>
                    <div className="grid grid-cols-2 gap-1 p-2">
                      {[
                        CHANNEL_KEYS.TRAVEL,
                        CHANNEL_KEYS.KKDAY,
                        CHANNEL_KEYS.KLOOK,
                        CHANNEL_KEYS.AGODA,
                      ].map(key => (
                        <Link
                          key={key}
                          href={`/credit-cards/channel/${key}`}
                          className="flex items-center px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400 rounded-md transition-colors cursor-pointer"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                          {CHANNEL_NAMES[key]}
                        </Link>
                      ))}
                    </div>

                    {/* Dining */}
                    <div className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700 mt-2">
                      {CHANNEL_NAMES[CHANNEL_KEYS.DINING]}
                    </div>
                    <div className="grid grid-cols-2 gap-1 p-2">
                      {[
                        CHANNEL_KEYS.DINING,
                        CHANNEL_KEYS.FOOD_DELIVERY,
                        CHANNEL_KEYS.RESTAURANT,
                      ].map(key => (
                        <Link
                          key={key}
                          href={`/credit-cards/channel/${key}`}
                          className="flex items-center px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400 rounded-md transition-colors cursor-pointer"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                          {CHANNEL_NAMES[key]}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Mobile Blog Dropdown */}
              <div className="flex flex-col">
                <button
                  onClick={toggleMobileBlog}
                  className="flex justify-between items-center w-full text-left text-gray-700 dark:text-gray-200 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium  px-4 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <span>信用卡文章</span>
                </button>
              </div>

              {/* <Link
                href='/expiry'
                className='text-gray-700 dark:text-gray-200 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium  px-4 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800'
                onClick={() => setMobileMenuOpen(false)}
              >
                產品到期提醒工具
              </Link> */}
            </nav>
          </div>
        )}
      </div>
    </div>
  );
}
